import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import OnboardingCountrySelect from "./onboarding-country-select"

/**
 * Inertia render-prop `errors` shape that the parent step receives.
 *
 * Errors are flat dotted keys per BE TRD §"Errors object shape":
 *   - `errors["shipping_address.recipient_name"]: ["can't be blank"]`
 *   - `errors["shipping_address.street"]: ["…"]`
 *   - …etc.
 *
 * The fieldset reads only `shipping_address.*` keys; non-shipping errors
 * are the parent step's concern.
 */
export type OnboardingAddressErrors = Record<string, string[] | undefined>

export interface OnboardingAddressFormValues {
  recipient_name?: string
  street?: string
  street_line_2?: string
  postal_code?: string
  city?: string
  country?: string
}

export interface OnboardingAddressFieldsetProps {
  /** Echoed values from BE on validation re-render (`form_values.shipping_address`). */
  values: OnboardingAddressFormValues
  /** Full Inertia errors object (the parent passes the entire object;
   *  the fieldset reads only `shipping_address.*` namespaced keys). */
  errors: OnboardingAddressErrors
  /** Disabled state mirrors the parent form's `processing` flag. */
  disabled?: boolean
  className?: string
}

/**
 * OnboardingAddressFieldset — 6 inputs covering the structured shipping
 * address block on Step 1.
 *
 * Wire format (BE-locked, per BE TRD §"Form params namespacing"):
 *   - `name="shipping_address[recipient_name]"`
 *   - `name="shipping_address[street]"`
 *   - `name="shipping_address[street_line_2]"`  (optional)
 *   - `name="shipping_address[postal_code]"`
 *   - `name="shipping_address[city]"`
 *   - `name="shipping_address[country]"`
 *
 * The Vitest wire-format canary asserts the literal `name="…"` strings
 * — see `__tests__/onboarding-address-fieldset.test.tsx`. Catches any
 * Rails strong-params drift before Playwright does (project memory
 * `vitest_wire_format_canary` + `playwright_form_canary`).
 *
 * Country is rendered via `<OnboardingCountrySelect>` — shadcn Select
 * (Radix) wrapping a hidden input that ships the ISO-3166-1 alpha-2
 * value. Per BE TRD §"Data Model Delta" the wire value MUST match
 * `^[A-Z]{2}$` (column CHECK constraint). UX Spec §6 calls for a
 * `<Combobox>` with search on desktop; v1 ships the plain Select +
 * locale-aware labels, search deferred to a follow-up.
 */
export function OnboardingAddressFieldset({
  values,
  errors,
  disabled = false,
  className = "",
}: OnboardingAddressFieldsetProps) {
  const { t } = useTranslation()

  // Helper to read a namespaced error key — undefined = no error rendered.
  const errorFor = (suffix: string): string[] | undefined =>
    errors[`shipping_address.${suffix}`]

  return (
    <fieldset disabled={disabled} className={`grid gap-4 ${className}`}>
      <div className="grid gap-2">
        <Label htmlFor="onboarding-account-shipping-recipient-name">
          {t("signup.account.shipping_recipient_name_label")}
          {/* OKS-728 — required marker; native `required` dropped so blank
              submits reach the server for a translated inline error (rendered
              within the wizard step) instead of a native browser bubble. */}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </Label>
        <Input
          id="onboarding-account-shipping-recipient-name"
          name="shipping_address[recipient_name]"
          type="text"
          autoComplete="name"
          aria-required="true"
          defaultValue={values.recipient_name ?? ""}
          aria-invalid={errorFor("recipient_name") ? "true" : undefined}
          aria-describedby={
            errorFor("recipient_name")
              ? "onboarding-error-shipping-recipient-name"
              : undefined
          }
          data-testid="onboarding-account-shipping-recipient-name"
        />
        <InputError
          id="onboarding-error-shipping-recipient-name"
          messages={errorFor("recipient_name")}
          data-testid="onboarding-error-shipping-recipient-name"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="onboarding-account-shipping-street">
          {t("signup.account.shipping_street_label")}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </Label>
        <Input
          id="onboarding-account-shipping-street"
          name="shipping_address[street]"
          type="text"
          autoComplete="address-line1"
          aria-required="true"
          defaultValue={values.street ?? ""}
          aria-invalid={errorFor("street") ? "true" : undefined}
          aria-describedby={
            errorFor("street") ? "onboarding-error-shipping-street" : undefined
          }
          data-testid="onboarding-account-shipping-street"
        />
        <InputError
          id="onboarding-error-shipping-street"
          messages={errorFor("street")}
          data-testid="onboarding-error-shipping-street"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="onboarding-account-shipping-street-line-2">
          {t("signup.account.shipping_street_line_2_label")}
        </Label>
        <Input
          id="onboarding-account-shipping-street-line-2"
          name="shipping_address[street_line_2]"
          type="text"
          autoComplete="address-line2"
          defaultValue={values.street_line_2 ?? ""}
          aria-invalid={errorFor("street_line_2") ? "true" : undefined}
          aria-describedby={
            errorFor("street_line_2")
              ? "onboarding-error-shipping-street-line-2"
              : undefined
          }
          data-testid="onboarding-account-shipping-street-line-2"
        />
        <InputError
          id="onboarding-error-shipping-street-line-2"
          messages={errorFor("street_line_2")}
          data-testid="onboarding-error-shipping-street-line-2"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_2fr]">
        <div className="grid gap-2">
          <Label htmlFor="onboarding-account-shipping-postal-code">
            {t("signup.account.shipping_postal_code_label")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id="onboarding-account-shipping-postal-code"
            name="shipping_address[postal_code]"
            type="text"
            autoComplete="postal-code"
            aria-required="true"
            defaultValue={values.postal_code ?? ""}
            aria-invalid={errorFor("postal_code") ? "true" : undefined}
            aria-describedby={
              errorFor("postal_code")
                ? "onboarding-error-shipping-postal-code"
                : undefined
            }
            data-testid="onboarding-account-shipping-postal-code"
          />
          <InputError
            id="onboarding-error-shipping-postal-code"
            messages={errorFor("postal_code")}
            data-testid="onboarding-error-shipping-postal-code"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="onboarding-account-shipping-city">
            {t("signup.account.shipping_city_label")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id="onboarding-account-shipping-city"
            name="shipping_address[city]"
            type="text"
            autoComplete="address-level2"
            aria-required="true"
            defaultValue={values.city ?? ""}
            aria-invalid={errorFor("city") ? "true" : undefined}
            aria-describedby={
              errorFor("city") ? "onboarding-error-shipping-city" : undefined
            }
            data-testid="onboarding-account-shipping-city"
          />
          <InputError
            id="onboarding-error-shipping-city"
            messages={errorFor("city")}
            data-testid="onboarding-error-shipping-city"
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="onboarding-account-shipping-country">
          {t("signup.account.shipping_country_label")}
          {/* OKS-728 — required marker. Country is server-validated
              (ShippingAddress presence); the `required` prop below maps to
              `aria-required` on the trigger (no native attribute). */}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </Label>
        <OnboardingCountrySelect
          id="onboarding-account-shipping-country"
          testId="onboarding-account-shipping-country"
          name="shipping_address[country]"
          defaultValue={values.country ?? ""}
          errorMessages={errorFor("country")}
          errorId="onboarding-error-shipping-country"
          disabled={disabled}
          required
        />
        <InputError
          id="onboarding-error-shipping-country"
          messages={errorFor("country")}
          data-testid="onboarding-error-shipping-country"
        />
      </div>
    </fieldset>
  )
}

export default OnboardingAddressFieldset
